import { useStores } from 'mobx/hooks/useStores';

import { FEATURES } from 'constants/features';

import Ticket from 'models/Ticket';
import { TicketTypeCreationAllowance, TicketTypeKind } from 'models/TicketTypes';

import { useDoctorsAndAssignees } from './useDoctor';
import { useIsConnectingTicketsToCall } from './useIsConnectingTicketsToCall';
import { usePatientMessagingStatus } from './usePatientMessagingStatus';

export const useMessagingQuickActionVisibility = (ticket: Ticket) => {
  const isConnectingTicketsToCall = useIsConnectingTicketsToCall();
  const { settingsStore, locationsStore, ticketTypesStore } = useStores();
  const { isCurrentDoctorAssigned } = useDoctorsAndAssignees(ticket);
  const { isSmsOptedOut, isLandline, hasMessagingConsent, patient } =
    usePatientMessagingStatus(ticket);

  // Common conditions
  if (
    !settingsStore.hasFeature(FEATURES.QUICK_ACTIONS) ||
    isConnectingTicketsToCall ||
    isLandline ||
    ticket.isClosedOrResolved ||
    // Check active conversation elsewhere
    (patient?.openConversationTicketId && patient.openConversationTicketId !== ticket.id)
  )
    return false;

  const kind = ticket.operatorTicket?.kind;

  if (
    patient?.isLocal &&
    kind !== TicketTypeKind.symptom &&
    (isCurrentDoctorAssigned || !ticket.mainAssign)
  )
    return true;

  // Common conditions for tasks and tickets
  if (
    isSmsOptedOut ||
    !hasMessagingConsent ||
    // Hide if assigned to someone else (not current user, not unassigned)
    (ticket.mainAssign && !isCurrentDoctorAssigned)
  )
    return false;

  // Task-specific checks
  if (ticket.isTask)
    // Hide if feature flags are disabled (need BOTH)
    return settingsStore.hasAllFeatures([
      FEATURES.PATIENT_MESSAGING,
      FEATURES.PATIENT_TASK_MESSAGING
    ]);

  // Pre-calculate for tickets
  const location = locationsStore.getLocationById(ticket.operatorTicket?.locationId);
  const hasMessages = ticket.messagesCount > 0;
  const subTypeId = ticket.operatorTicket?.subTicketTypeIds?.[0];

  const ticketType =
    typeof subTypeId === 'number' && !!kind
      ? ticketTypesStore.getTicketTypeByKind(subTypeId, kind)
      : null;

  const isTicketTypeMessagingEnabled = ticketType?.displaySettings?.includes(
    TicketTypeCreationAllowance.MESSAGING
  );

  // Ticket-specific checks
  return (
    settingsStore.hasSomeFeatures([
      FEATURES.PATIENT_MESSAGING,
      FEATURES.ACTIONS_SEND_PATIENT_SMS
    ]) &&
    // Hide if location messaging disabled
    location?.isMessagingEnabled &&
    // Hide if no ticket type
    subTypeId !== undefined &&
    // Show if messaging is in progress OR ticket type supports messaging
    (hasMessages || isTicketTypeMessagingEnabled)
  );
};
